import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, Typography, Card, Tag, Progress, Badge, Empty } from "antd";
import {
  FileProtectOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  UserOutlined,
  LoadingOutlined,
  TagsOutlined,
  DollarOutlined,
  FilePdfOutlined,
  EditOutlined,
  PlusCircleOutlined,
  EyeOutlined,
  SyncOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import api from "../../../axiosConfig";
import "./HistorialControlServicioDialog.css";

const { Text, Title } = Typography;

/**
 * Componente HistorialControlServicioDialog para mostrar el historial de un control de servicio
 * con diseño moderno de proceso flow.
 */
function HistorialControlServicioDialog({ open, onClose, controlServicioId, codigoPcs }) {
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (controlServicioId && open) {
      setLoading(true);
      api
        .get(`/control-servicios/${controlServicioId}/historial`)
        .then((response) => {
          if (response.data.success) {
            setHistorial(response.data.data || []);
          } else {
            setHistorial([]);
          }
        })
        .catch((error) => {
          console.error("Error al obtener el historial:", error);
          setHistorial([]);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [controlServicioId, open]);

  // Definir las acciones con sus iconos y colores
  const actionConfig = {
    // Novedad "firma ilegible del paciente" (van primero: sus textos contienen "validación"/"validada")
    'firma_enviada': {
      label: 'Firma Enviada a Validar',
      icon: <ExclamationCircleOutlined />,
      color: '#c41d7f',
      keywords: ['enviado a validación de firma']
    },
    'firma_validada': {
      label: 'Firma Validada por OTP',
      icon: <CheckCircleOutlined />,
      color: '#722ed1',
      keywords: ['firma validada por otp']
    },
    'firma_cancelada': {
      label: 'Validación de Firma Cancelada',
      icon: <ExclamationCircleOutlined />,
      color: '#ff4d4f',
      keywords: ['validación de firma cancelada']
    },
    'creacion': {
      label: 'Creación',
      icon: <PlusCircleOutlined />,
      color: '#52c41a',
      keywords: ['creó', 'creado', 'creacion', 'generó', 'generado']
    },
    'recibido': {
      label: 'Recibido',
      icon: <TagsOutlined />,
      color: '#13c2c2',
      keywords: ['recibido', 'marcó como recibido']
    },
    'facturado': {
      label: 'Facturado',
      icon: <DollarOutlined />,
      color: '#1890ff',
      keywords: ['facturado', 'facturó', 'marcó como facturado']
    },
    'pdf': {
      label: 'PDF Generado',
      icon: <FilePdfOutlined />,
      color: '#722ed1',
      keywords: ['pdf', 'descargó', 'generó pdf']
    },
    'modificacion': {
      label: 'Modificación',
      icon: <EditOutlined />,
      color: '#fa8c16',
      keywords: ['modificó', 'editó', 'actualizó', 'cambió']
    },
    'visualizacion': {
      label: 'Visualización',
      icon: <EyeOutlined />,
      color: '#8c8c8c',
      keywords: ['visualizó', 'vio', 'consultó']
    },
    'etiqueta_quitada': {
      label: 'Etiqueta Quitada',
      icon: <ExclamationCircleOutlined />,
      color: '#ff4d4f',
      keywords: ['quitó etiqueta', 'removió', 'eliminó estado']
    },
    'validacion': {
      label: 'Validación',
      icon: <CheckCircleOutlined />,
      color: '#52c41a',
      keywords: ['validó', 'validado', 'aprobó']
    },
  };

  const getActionConfig = (accion) => {
    if (!accion) return { label: 'Acción', icon: <SyncOutlined />, color: '#8c8c8c' };

    const accionLower = accion.toLowerCase();

    for (const [key, config] of Object.entries(actionConfig)) {
      if (config.keywords.some(keyword => accionLower.includes(keyword))) {
        return config;
      }
    }

    return { label: accion, icon: <SyncOutlined />, color: '#8c8c8c' };
  };

  const formatearFecha = (fecha) => {
    const date = new Date(fecha);
    const ahora = new Date();
    const diferencia = ahora - date;
    const minutos = Math.floor(diferencia / 60000);
    const horas = Math.floor(diferencia / 3600000);
    const dias = Math.floor(diferencia / 86400000);

    if (minutos < 1) return "Hace un momento";
    if (minutos < 60) return `Hace ${minutos} min`;
    if (horas < 24) return `Hace ${horas}h`;
    if (dias < 7) return `Hace ${dias}d`;
    return date.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  // Calcular el progreso basado en el estado
  const calculateProgress = () => {
    if (!historial || historial.length === 0) return 0;

    const tieneFacturado = historial.some(h =>
      h.accion?.toLowerCase().includes('facturado') ||
      h.accion?.toLowerCase().includes('facturó')
    );
    const tieneRecibido = historial.some(h =>
      h.accion?.toLowerCase().includes('recibido')
    );

    if (tieneFacturado) return 100;
    if (tieneRecibido) return 50;
    return 25;
  };

  const getProgressStatus = () => {
    const progress = calculateProgress();
    if (progress === 100) return 'success';
    return 'active';
  };

  const getCurrentState = () => {
    if (!historial || historial.length === 0) return null;

    // Buscar el último estado significativo
    for (const record of historial) {
      const accionLower = record.accion?.toLowerCase() || '';
      if (accionLower.includes('facturado') || accionLower.includes('facturó')) {
        return { label: 'Facturado', color: '#1890ff' };
      }
      if (accionLower.includes('recibido')) {
        return { label: 'Recibido', color: '#13c2c2' };
      }
    }

    return { label: 'Creado', color: '#52c41a' };
  };

  const currentState = getCurrentState();
  const progress = calculateProgress();
  const progressStatus = getProgressStatus();

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div className="historial-cs-modal-header">
          <div className="header-icon">
            <FileProtectOutlined />
          </div>
          <div className="header-content">
            <Title level={4} className="header-title">
              Historial del Control de Servicio
            </Title>
            <Text type="secondary" className="header-subtitle">
              Seguimiento de auditoría • {codigoPcs || `ID: ${controlServicioId}`}
            </Text>
          </div>
          {currentState && (
            <Badge
              count={currentState.label}
              style={{
                backgroundColor: currentState.color,
                fontSize: '12px',
                fontWeight: 500,
                height: '24px',
                lineHeight: '24px',
                minWidth: '80px'
              }}
            />
          )}
        </div>
      }
      footer={null}
      width={800}
      centered
      className="historial-cs-modal"
      styles={{
        body: { padding: 0 }
      }}
    >
      <div className="historial-cs-container">
        {/* Progress Header */}
        <div className="progress-section">
          <div className="progress-info">
            <Text className="progress-label">Estado de Auditoría</Text>
            <Text className="progress-percentage">{progress}%</Text>
          </div>
          <Progress
            percent={progress}
            status={progressStatus}
            strokeColor={{
              '0%': '#52c41a',
              '50%': '#13c2c2',
              '100%': '#1890ff'
            }}
            className="progress-bar"
          />
          <div className="progress-stages">
            <span className={progress >= 25 ? 'active' : ''}>Creado</span>
            <span className={progress >= 50 ? 'active' : ''}>Recibido</span>
            <span className={progress >= 100 ? 'active' : ''}>Facturado</span>
          </div>
        </div>

        {/* Timeline Events */}
        <div className="timeline-section">
          <Title level={5} className="timeline-title">
            Registro de Actividades
          </Title>

          {loading ? (
            <div className="loading-state">
              <LoadingOutlined style={{ fontSize: '24px', marginBottom: '16px' }} />
              <Text>Cargando historial...</Text>
            </div>
          ) : (!historial || historial.length === 0) ? (
            <Empty
              description="No hay eventos en el historial"
              style={{ padding: '40px 0' }}
            />
          ) : (
            <div className="timeline-events">
              {historial.map((registro, index) => {
                const config = getActionConfig(registro.accion);
                const isLastEvent = index === historial.length - 1;

                return (
                  <div key={registro.id || index} className={`timeline-event ${isLastEvent ? 'last' : ''}`}>
                    <div className="event-connector">
                      <div className="event-dot" style={{ backgroundColor: config.color }}>
                        {React.cloneElement(config.icon, { style: { fontSize: '14px', color: '#fff' } })}
                      </div>
                      {!isLastEvent && <div className="event-line" />}
                    </div>

                    <Card className="event-card">
                      <div className="event-header">
                        <div className="event-actor">
                          <div className="actor-icon">
                            <UserOutlined style={{ color: '#52c41a' }} />
                          </div>
                          <div className="actor-info">
                            <Text strong className="actor-name">
                              {registro.usuario?.nombre || 'Sistema'}
                            </Text>
                            <Text type="secondary" className="actor-role">
                              Usuario
                            </Text>
                          </div>
                        </div>

                        <div className="event-time">
                          <Text type="secondary" className="time-relative">
                            {formatearFecha(registro.fecha)}
                          </Text>
                          <Text type="secondary" className="time-absolute">
                            {new Date(registro.fecha).toLocaleString('es-ES', {
                              hour: '2-digit',
                              minute: '2-digit',
                              day: '2-digit',
                              month: '2-digit'
                            })}
                          </Text>
                        </div>
                      </div>

                      <div className="event-content">
                        <div className="event-action">
                          <Tag
                            color={config.color}
                            className="action-tag"
                          >
                            {registro.accion}
                          </Tag>
                        </div>

                        {registro.descripcion && (
                          <Text className="event-description">
                            {registro.descripcion}
                          </Text>
                        )}
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

HistorialControlServicioDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  controlServicioId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  codigoPcs: PropTypes.string,
};

export default HistorialControlServicioDialog;
